"use client";
import { useSession } from "next-auth/react";
import {
  ReactNode,
  Suspense,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { customerNotificationsService } from "~/connectors/notifications.connector";
import type { Notification } from "~/types";
import NotifyParamHandler from "./NotifyParamHandler";
import PushNotificationHandler from "./PushNotificationHandler";


interface NotificationsContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

interface NotificationsProviderProps {
  children: ReactNode;
}

export function NotificationsProvider({
  children,
}: NotificationsProviderProps) {
  const { status } = useSession();
  const isAuthenticated = status === "authenticated";
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setIsLoading(true);
      const response = await customerNotificationsService.getNotifications();
      setNotifications(response.data);
    } catch {
      // Silently ignore errors (e.g. unauthenticated)
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      hasFetchedRef.current = false;
      return;
    }
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    refresh();
  }, [isAuthenticated, refresh]);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <NotificationsContext.Provider
      value={{ notifications, unreadCount, isLoading, refresh }}
    >
      <PushNotificationHandler />
      <Suspense>
        <NotifyParamHandler refresh={refresh} />
      </Suspense>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsContextType {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error(
      "useNotifications must be used within a NotificationsProvider",
    );
  }
  return context;
}
